// ENG-96 — "sticky" pill exemption for rows this surface just acted on.
//
// Opening an email optimistically patches isRead:true into the ["inbox"] cache in the
// same React batch as the open, so every surface re-derives its visible list and the row
// drops out of a non-"all" pill before its body can render. The patch is correct and must
// stay (it keeps the unread badges honest); the consumers are what needed fixing.
//
// Sticky is NOT "rows that are open". It is "rows this surface optimistically patched
// during the current epoch". That reframing is what makes this work for every pill:
// un-starring under Flagged and un-claiming under Mine never touch expansion state, so an
// expansion-keyed design would silently cover Unread only.
//
// Search is deliberately NOT an input — sticky overrides the pill and nothing else, so a
// row you opened still leaves the list when you type a query it does not match (the
// reading pane keeps showing it).
//
// The set needs no eviction policy: it is bounded by one id per row the user touches
// within a single epoch, and an epoch ends on any pill or list-scope change.

import { matchesPill, type EmailPill } from "./filters";
import type { UnifiedEmail } from "./types";

export type StickyIds = ReadonlySet<string>;

export const EMPTY_STICKY: StickyIds = new Set<string>();

/** The pill predicate with an Outlook-style exemption: a row the user has already acted on
 *  in THIS view stays listed even once that action made it stop matching. */
export function matchesPillSticky(
  email: UnifiedEmail,
  pill: EmailPill,
  currentUserId: string,
  sticky: StickyIds,
): boolean {
  return sticky.has(email.id) || matchesPill(email, pill, currentUserId);
}

/** Monotonic add. Returns the SAME set reference when the id is already present, so callers
 *  can skip a re-render — load-bearing, see useStickyPill.mark. Never mutates its input. */
export function addSticky(current: StickyIds, id: string): StickyIds {
  if (current.has(id)) return current;
  const next = new Set(current);
  next.add(id);
  return next;
}

/** Decision 4: the pop-out's pill KEYS are historical ("flagged"); the shared vocabulary is
 *  EmailPill ("starred"). Visible labels are untouched — only the predicate input is
 *  translated, so this is a convergence, not a rename. The pop-out has no "unclaimed". */
export const POPOUT_PILL_TO_SHARED: Record<"all" | "unread" | "flagged" | "mine", EmailPill> = {
  all: "all",
  unread: "unread",
  flagged: "starred",
  mine: "mine",
};
